<header
  class="relative z-30 flex min-w-0 items-center justify-between gap-3 overflow-visible bg-brand-navBar h-17 ps-4 pe-4 shadow-header border-brand-shadow md:ps-6 md:pe-6"
>
  <img
    src="/assets/images/CNH_capitals.png"
    [alt]="ALT_TEXT.CNH_LOGO"
    class="h-9 w-auto max-w-[7.5rem] shrink-0 object-contain md:h-11"
    width="120"
    height="44"
    loading="eager"
    fetchpriority="high"
  />

  <section
    class="flex min-w-0 shrink items-center justify-end gap-2 md:gap-3"
    aria-label="Notifications and account"
  >
    <!-- Notification Bell -->

    <div class="relative shrink-0">
      <button
        type="button"
        class="relative cursor-pointer border-none bg-transparent p-2"
        [attr.aria-label]="
          notificationCount() > 0
            ? 'Notifications, ' + notificationCount() + ' unread'
            : 'Notifications'
        "
        aria-haspopup="true"
        [attr.aria-expanded]="isNotificationDropdownOpen()"
        (click)="toggleNotificationDropdown()"
      >
        <img
          [src]="IMAGE_PATHS.NOTIFICATION"
          [alt]="ALT_TEXT.NOTIFICATION_ICON"
          width="24"
          height="24"
          loading="eager"
          class="brightness-0 invert"
        />
        <span
          class="absolute right-0 top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-center text-xs text-white transition-opacity duration-150"
          [class.bg-secondary-mustard]="notificationCount() > 0"
          [class.opacity-0]="notificationCount() === 0"
          [class.pointer-events-none]="notificationCount() === 0"
          aria-hidden="true"
          >{{ notificationCount() > 0 ? notificationCount() : '' }}</span
        >
      </button>

      @if (isNotificationDropdownOpen()) {
        <app-notification-dropdown
          panelClass="absolute left-1/2 top-full z-50 mt-2 w-72 -translate-x-1/2 sm:w-80 lg:w-96"
          listClass="max-h-96 gap-6"
          itemGapClass="gap-4"
          messageClass="leading-8"
          emptyStateClass="min-h-40 px-6"
          footerClass="text-base"
          (panelClose)="closeNotificationDropdown()"
          (viewAll)="navigateToNotifications()"
          (notificationsCleared)="onNotificationsCleared()"
        ></app-notification-dropdown>
      }
    </div>

    <div class="mx-1 h-6 w-px shrink-0 bg-brand-white/30 md:mx-3"></div>

    <!-- Avatar -->

    <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-white">
      <img
        [src]="IMAGE_PATHS.PROFILE_DARK"
        [alt]="ALT_TEXT.USER_PROFILE"
        width="20"
        height="20"
        loading="eager"
        class="filter-navbar-icon"
      />
    </div>

    <!-- User Profile with Dropdown -->

    <div class="relative min-w-0">
      <button
        type="button"
        class="ms-1 flex min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-sm border-none p-2 md:gap-3"
        [ngClass]="isDropdownOpen() ? 'bg-brand-white/10' : 'bg-transparent'"
        (click)="toggleDropdown()"
        aria-haspopup="true"
        [attr.aria-expanded]="isDropdownOpen()"
        aria-label="User menu"
        (keydown.enter)="toggleDropdown()"
        (keydown.space)="toggleDropdown(); $event.preventDefault()"
      >
        <!-- User Info -->

        <div
          class="flex min-w-0 max-w-[6.5rem] flex-col text-left sm:max-w-[9rem] md:max-w-[11rem] lg:max-w-[14rem] xl:max-w-none"
        >
          @if (partySearchContext.params()) {
            <span class="truncate text-xs text-white/70">
              {{ COMMON_CONSTANTS.IMPERSONATED_USER }}:
            </span>

            <span class="truncate text-sm font-semibold text-white">{{ displayUserName() }}</span>

            @if (displayUserEmail()) {
              <span class="hidden truncate text-xs font-normal text-white/70 sm:block">{{
                displayUserEmail()
              }}</span>
            }
          } @else {
            <div class="flex min-w-0 gap-1">
              <span class="truncate text-sm font-semibold text-white">{{
                userProfile().name
              }}</span>

              <img
                class="shrink-0"
                [src]="IMAGE_PATHS.EDIT_YELLOW"
                [alt]="ALT_TEXT.EDIT_ICON"
                width="16"
                height="16"
                loading="eager"
              />
            </div>

            @if (userProfile().email) {
              <span class="truncate text-xs font-normal text-white/70">{{
                userProfile().email
              }}</span>
            }
          }
        </div>

        <!-- Chevron -->

        <img
          [src]="IMAGE_PATHS.ARROW_DOWN"
          [alt]="ALT_TEXT.ARROW_DOWN"
          class="shrink-0 transition-transform duration-200 brightness-0 invert"
          [class.rotate-180]="isDropdownOpen()"
          width="16"
          height="16"
          loading="eager"
        />
      </button>

      <!-- Dropdown Menu -->

      @if (isDropdownOpen()) {
        <div
          id="user-menu"
          class="absolute right-0 top-full z-[100] mt-1 w-40 rounded-bl-sm rounded-br-sm bg-brand-white py-2 shadow-lg"
          role="menu"
        >
          <!-- My Profile -->

          <button
            class="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-4 py-3 text-sm transition-colors hover:bg-brand-whiteSmoke"
            (click)="onMyProfile()"
            role="menuitem"
            [attr.aria-label]="HEADER_CONST.myProfile"
          >
            <img
              [src]="IMAGE_PATHS.PROFILE_DARK"
              [alt]="ALT_TEXT.MY_PROFILE_ICON"
              width="16"
              height="16"
              loading="lazy"
              class="theme-icon"
            />

            <span class="text-sm font-light text-brand-primary">{{ HEADER_CONST.myProfile }}</span>
          </button>

          <!-- Dark Mode -->

          <button
            class="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-4 py-3 text-sm transition-colors hover:bg-brand-whiteSmoke"
            (click)="onDarkMode()"
            role="menuitem"
            [attr.aria-label]="HEADER_CONST.darkMode"
          >
            <img
              [src]="IMAGE_PATHS.HALF_MOON"
              [alt]="ALT_TEXT.DARK_MODE"
              width="16"
              height="16"
              loading="lazy"
              class="theme-icon"
            />

            <span class="text-sm font-light text-brand-primary">{{ themeService.label() }}</span>
          </button>

          <!-- Logout -->

          <button
            class="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-4 py-3 text-sm transition-colors hover:bg-brand-whiteSmoke"
            (click)="onLogout()"
            role="menuitem"
            [attr.aria-label]="HEADER_CONST.logout"
          >
            <img
              [src]="IMAGE_PATHS.LOG_OUT"
              [alt]="ALT_TEXT.LOGOUT"
              width="16"
              height="16"
              loading="lazy"
              class="theme-icon"
            />

            <span class="text-sm font-light text-brand-primary">{{ HEADER_CONST.logout }}</span>
          </button>
        </div>
      }
    </div>
  </section>
</header>
